import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Description as PdfIcon,
  Edit as EditIcon,
  Policy as PolicyIcon,
  Search as SearchIcon,
  UploadFile as UploadIcon
} from '@mui/icons-material';
import StaffingEmployeeSelect from './StaffingEmployeeSelect';

const API_URL = process.env.REACT_APP_API_URL || '';
const TEAL = '#0d9488';
const TEAL_DARK = '#0f766e';

const thSx = {
  fontWeight: 700,
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: '#64748b',
  bgcolor: '#f8fafc',
  borderBottom: '1px solid #e2e8f0',
  py: 1.25
};

const emptyAssignForm = { candidate_id: '', casual_quota: '', sick_quota: '', other_quota: '' };

function quotaCell(summary, type, fallback) {
  const q = summary?.[type]?.quota ?? fallback;
  const a = summary?.[type]?.available;
  return a != null ? `${q} (${a} left)` : String(q ?? '—');
}

export default function StaffingLeavePoliciesTab({ apiRequest, token, activeEmployeesList, onFeedback }) {
  const [section, setSection] = useState(0);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);

  const [leavePolicyDoc, setLeavePolicyDoc] = useState(null);
  const [leavePolicyPdfFile, setLeavePolicyPdfFile] = useState(null);
  const [leavePolicyUploading, setLeavePolicyUploading] = useState(false);

  const [defaultLeavePolicy, setDefaultLeavePolicy] = useState({
    casual_quota: '12',
    sick_quota: '6',
    other_quota: '3'
  });
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [quotaSearch, setQuotaSearch] = useState('');
  const [leaveBulkFile, setLeaveBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);

  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignLockEmployee, setAssignLockEmployee] = useState(false);
  const [assignForm, setAssignForm] = useState(emptyAssignForm);
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [lp, pol, lb] = await Promise.all([
        apiRequest('/api/company/hrms/leave-policy/document'),
        apiRequest(`/api/company/hrms/leave-policy/default?year=${year}`),
        apiRequest(`/api/company/hrms/leave-balances?year=${year}`)
      ]);
      setLeavePolicyDoc(lp.document || null);
      if (pol.policy) {
        setDefaultLeavePolicy({
          casual_quota: String(pol.policy.casual_quota ?? ''),
          sick_quota: String(pol.policy.sick_quota ?? ''),
          other_quota: String(pol.policy.other_quota ?? '')
        });
      }
      setLeaveBalances(lb.balances || []);
    } catch (e) {
      onFeedback(e.message);
    } finally {
      setLoading(false);
    }
  }, [apiRequest, year, onFeedback]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const balanceByCandidate = useMemo(() => {
    const m = new Map();
    leaveBalances.forEach((b) => m.set(b.candidate_id, b));
    return m;
  }, [leaveBalances]);

  const quotaRows = useMemo(() => {
    return activeEmployeesList.map((emp) => {
      const b = balanceByCandidate.get(emp.id);
      return {
        candidate_id: emp.id,
        employee_name: emp.full_name || emp.name,
        employee_code: emp.employee_code,
        mobile: emp.mobile,
        configured: !!b,
        casual_quota: b?.casual_quota,
        sick_quota: b?.sick_quota,
        other_quota: b?.other_quota,
        balance_summary: b?.balance_summary
      };
    });
  }, [activeEmployeesList, balanceByCandidate]);

  const filteredQuotaRows = useMemo(() => {
    const q = quotaSearch.trim().toLowerCase();
    if (!q) return quotaRows;
    return quotaRows.filter((row) =>
      [row.employee_name, row.employee_code, row.mobile].filter(Boolean).join(' ').toLowerCase().includes(q)
    );
  }, [quotaRows, quotaSearch]);

  const stats = useMemo(() => {
    const configured = quotaRows.filter((r) => r.configured).length;
    return {
      employees: quotaRows.length,
      configured,
      unconfigured: Math.max(0, quotaRows.length - configured)
    };
  }, [quotaRows]);

  const openAssignDialog = (row = null) => {
    setAssignLockEmployee(!!row?.candidate_id);
    if (row?.candidate_id) {
      setAssignForm({
        candidate_id: row.candidate_id,
        casual_quota: row.casual_quota != null ? String(row.casual_quota) : defaultLeavePolicy.casual_quota,
        sick_quota: row.sick_quota != null ? String(row.sick_quota) : defaultLeavePolicy.sick_quota,
        other_quota: row.other_quota != null ? String(row.other_quota) : defaultLeavePolicy.other_quota
      });
    } else {
      setAssignForm({
        ...emptyAssignForm,
        casual_quota: defaultLeavePolicy.casual_quota,
        sick_quota: defaultLeavePolicy.sick_quota,
        other_quota: defaultLeavePolicy.other_quota
      });
    }
    setAssignDialogOpen(true);
  };

  const saveAssignment = async () => {
    if (!assignForm.candidate_id) {
      onFeedback('Select an employee.');
      return;
    }
    setSaving(true);
    try {
      await apiRequest('/api/company/hrms/leave-balances', {
        method: 'POST',
        body: JSON.stringify({
          candidate_id: Number(assignForm.candidate_id),
          year,
          casual_quota: Number(assignForm.casual_quota) || 0,
          sick_quota: Number(assignForm.sick_quota) || 0,
          other_quota: Number(assignForm.other_quota) || 0
        })
      });
      onFeedback('Leave quotas saved.');
      setAssignDialogOpen(false);
      setAssignForm(emptyAssignForm);
      await loadAll();
    } catch (e) {
      onFeedback(e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveDefaultLeavePolicy = async (applyToAll = false) => {
    try {
      const data = await apiRequest('/api/company/hrms/leave-policy/default', {
        method: 'POST',
        body: JSON.stringify({
          year,
          casual_quota: Number(defaultLeavePolicy.casual_quota) || 0,
          sick_quota: Number(defaultLeavePolicy.sick_quota) || 0,
          other_quota: Number(defaultLeavePolicy.other_quota) || 0,
          apply_to_all_active: applyToAll
        })
      });
      if (applyToAll && data.applyReport) {
        onFeedback(`Default saved and applied to ${data.applyReport.applied} active employee(s) for ${year}.`);
      } else {
        onFeedback(`Default leave quotas saved for ${year}.`);
      }
      await loadAll();
    } catch (e) {
      onFeedback(e.message);
    }
  };

  const applyDefaultToAll = async () => {
    try {
      const data = await apiRequest('/api/company/hrms/leave-policy/default/apply-all', {
        method: 'POST',
        body: JSON.stringify({ year, overwrite: true })
      });
      onFeedback(`Applied default to ${data.applied} employee(s) for ${year}.`);
      await loadAll();
    } catch (e) {
      onFeedback(e.message);
    }
  };

  const uploadLeavePolicyPdf = async () => {
    if (!leavePolicyPdfFile) {
      onFeedback('Choose a PDF file first.');
      return;
    }
    setLeavePolicyUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', leavePolicyPdfFile);
      const res = await fetch(`${API_URL}/api/company/hrms/leave-policy/document`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      onFeedback(data.message || 'Leave policy PDF uploaded.');
      setLeavePolicyPdfFile(null);
      await loadAll();
    } catch (e) {
      onFeedback(e.message);
    } finally {
      setLeavePolicyUploading(false);
    }
  };

  const removeLeavePolicyPdf = async () => {
    if (!window.confirm('Remove the leave policy PDF?')) return;
    try {
      await apiRequest('/api/company/hrms/leave-policy/document', { method: 'DELETE' });
      onFeedback('Leave policy PDF removed.');
      await loadAll();
    } catch (e) {
      onFeedback(e.message);
    }
  };

  const downloadBulkTemplate = async () => {
    const res = await fetch(`${API_URL}/api/company/hrms/leave-balances/bulk-template`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = 'leave_balances_template.csv';
    a.click();
    URL.revokeObjectURL(blobUrl);
  };

  const uploadBulk = async () => {
    if (!leaveBulkFile) return;
    setBulkUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', leaveBulkFile);
      fd.append('year', String(year));
      const res = await fetch(`${API_URL}/api/company/hrms/leave-balances/bulk`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      onFeedback(`Bulk upload: ${data.updated || 0} employee quota(s) updated.`);
      setLeaveBulkFile(null);
      await loadAll();
    } catch (e) {
      onFeedback(e.message);
    } finally {
      setBulkUploading(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={1}>
        <Box>
          <Typography variant="h6" fontWeight={800} color="#0f172a">
            Leave policies
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Policy PDF for the app · default quotas · per-employee overrides for {year}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField size="small" type="number" label="Policy year" value={year} onChange={(e) => setYear(Number(e.target.value))} sx={{ width: 110 }} />
          <Chip size="small" label={`${stats.configured}/${stats.employees} quotas set`} color={stats.unconfigured ? 'warning' : 'success'} variant="outlined" />
        </Stack>
      </Stack>

      {stats.unconfigured > 0 && section === 2 && !loading && (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          {stats.unconfigured} active employee(s) have no leave quotas for {year}. Set default and apply to all, or assign individually.
        </Alert>
      )}

      <Card variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 1px 3px rgba(15,23,42,0.06)' }}>
        <Tabs
          value={section}
          onChange={(_, v) => setSection(v)}
          sx={{
            px: 2,
            borderBottom: '1px solid #e2e8f0',
            bgcolor: '#fafafa',
            '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', minHeight: 48 },
            '& .Mui-selected': { color: TEAL }
          }}
        >
          <Tab icon={<PdfIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Policy PDF" />
          <Tab icon={<PolicyIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Default quotas" />
          <Tab label="Employee quotas" />
        </Tabs>

        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          {section === 0 && (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Employees view this PDF in the mobile app (Documents → Leave policy) and on web when applying leave.
              </Typography>
              {leavePolicyDoc ? (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={thSx}>File</TableCell>
                        <TableCell sx={thSx}>Uploaded</TableCell>
                        <TableCell sx={thSx} align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <Typography fontWeight={600}>{leavePolicyDoc.original_filename}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {leavePolicyDoc.uploaded_at ? new Date(leavePolicyDoc.uploaded_at).toLocaleString() : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {leavePolicyDoc.view_url && (
                            <Button size="small" variant="outlined" href={leavePolicyDoc.view_url} target="_blank" rel="noreferrer" sx={{ mr: 0.5 }}>
                              Preview
                            </Button>
                          )}
                          <Button size="small" color="error" variant="outlined" onClick={removeLeavePolicyPdf}>
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info" variant="outlined">No leave policy PDF uploaded yet.</Alert>
              )}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap>
                <Button variant="outlined" component="label">
                  Choose PDF
                  <input type="file" hidden accept="application/pdf,.pdf" onChange={(e) => setLeavePolicyPdfFile(e.target.files?.[0] || null)} />
                </Button>
                <Typography variant="body2" color="text.secondary">{leavePolicyPdfFile?.name || 'No file selected'}</Typography>
                <Button variant="contained" disabled={!leavePolicyPdfFile || leavePolicyUploading} onClick={uploadLeavePolicyPdf} sx={{ bgcolor: TEAL, '&:hover': { bgcolor: TEAL_DARK } }}>
                  {leavePolicyUploading ? 'Uploading…' : leavePolicyDoc ? 'Replace PDF' : 'Upload PDF'}
                </Button>
              </Stack>
            </Stack>
          )}

          {section === 1 && (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Default quotas apply when employees are activated. Use &ldquo;Apply to all active&rdquo; to refresh everyone for {year}.
              </Typography>
              <GridLikeDefaultForm
                defaultLeavePolicy={defaultLeavePolicy}
                setDefaultLeavePolicy={setDefaultLeavePolicy}
                onSave={() => saveDefaultLeavePolicy(false)}
                onSaveApplyAll={() => saveDefaultLeavePolicy(true)}
                onApplySaved={applyDefaultToAll}
              />
            </Stack>
          )}

          {section === 2 && (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap>
                <TextField
                  size="small"
                  placeholder="Search employees…"
                  value={quotaSearch}
                  onChange={(e) => setQuotaSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    )
                  }}
                  sx={{ maxWidth: 320, bgcolor: '#fff' }}
                />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button size="small" variant="outlined" startIcon={<UploadIcon />} onClick={() => openAssignDialog()}>
                    Set quotas
                  </Button>
                  <Button size="small" variant="text" onClick={downloadBulkTemplate}>CSV template</Button>
                  <Button size="small" variant="outlined" component="label">
                    CSV
                    <input type="file" hidden accept=".csv" onChange={(e) => setLeaveBulkFile(e.target.files?.[0] || null)} />
                  </Button>
                  <Button size="small" variant="contained" disabled={!leaveBulkFile || bulkUploading} onClick={uploadBulk} sx={{ bgcolor: TEAL }}>
                    {bulkUploading ? '…' : 'Upload CSV'}
                  </Button>
                </Stack>
              </Stack>
              {leaveBulkFile && (
                <Typography variant="caption" color="text.secondary">Selected: {leaveBulkFile.name}</Typography>
              )}

              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={thSx}>Employee</TableCell>
                      <TableCell sx={thSx} align="right">Casual</TableCell>
                      <TableCell sx={thSx} align="right">Sick</TableCell>
                      <TableCell sx={thSx} align="right">Other</TableCell>
                      <TableCell sx={thSx}>Status</TableCell>
                      <TableCell sx={thSx} align="right" width={72}>Edit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">Loading…</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {!loading && filteredQuotaRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No active employees match your search.</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {!loading &&
                      filteredQuotaRows.map((row) => (
                        <TableRow key={row.candidate_id} hover>
                          <TableCell>
                            <Typography fontWeight={600}>{row.employee_name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {[row.employee_code, row.mobile].filter(Boolean).join(' · ') || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{quotaCell(row.balance_summary, 'casual', row.casual_quota)}</TableCell>
                          <TableCell align="right">{quotaCell(row.balance_summary, 'sick', row.sick_quota)}</TableCell>
                          <TableCell align="right">{quotaCell(row.balance_summary, 'other', row.other_quota)}</TableCell>
                          <TableCell>
                            <Chip size="small" label={row.configured ? 'Configured' : 'Not set'} color={row.configured ? 'success' : 'default'} variant={row.configured ? 'filled' : 'outlined'} />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={row.configured ? 'Edit quotas' : 'Set quotas'}>
                              <IconButton size="small" onClick={() => openAssignDialog(row)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Employee leave quotas — {year}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <StaffingEmployeeSelect
              employees={activeEmployeesList}
              value={assignForm.candidate_id}
              onChange={(id) => setAssignForm((p) => ({ ...p, candidate_id: id }))}
              disabled={assignLockEmployee}
            />
            <Stack direction="row" spacing={2}>
              <TextField fullWidth size="small" label="Casual / year" type="number" value={assignForm.casual_quota} onChange={(e) => setAssignForm((p) => ({ ...p, casual_quota: e.target.value }))} />
              <TextField fullWidth size="small" label="Sick / year" type="number" value={assignForm.sick_quota} onChange={(e) => setAssignForm((p) => ({ ...p, sick_quota: e.target.value }))} />
              <TextField fullWidth size="small" label="Other / year" type="number" value={assignForm.other_quota} onChange={(e) => setAssignForm((p) => ({ ...p, other_quota: e.target.value }))} />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveAssignment} disabled={saving || !assignForm.candidate_id} sx={{ bgcolor: TEAL, '&:hover': { bgcolor: TEAL_DARK } }}>
            Save quotas
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function GridLikeDefaultForm({ defaultLeavePolicy, setDefaultLeavePolicy, onSave, onSaveApplyAll, onApplySaved }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#f0fdfa' }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField size="small" fullWidth label="Casual / year" type="number" value={defaultLeavePolicy.casual_quota} onChange={(e) => setDefaultLeavePolicy((p) => ({ ...p, casual_quota: e.target.value }))} />
          <TextField size="small" fullWidth label="Sick / year" type="number" value={defaultLeavePolicy.sick_quota} onChange={(e) => setDefaultLeavePolicy((p) => ({ ...p, sick_quota: e.target.value }))} />
          <TextField size="small" fullWidth label="Other / year" type="number" value={defaultLeavePolicy.other_quota} onChange={(e) => setDefaultLeavePolicy((p) => ({ ...p, other_quota: e.target.value }))} />
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button variant="contained" onClick={onSave} sx={{ bgcolor: TEAL, '&:hover': { bgcolor: TEAL_DARK } }}>
            Save default
          </Button>
          <Button variant="outlined" onClick={onSaveApplyAll}>
            Save &amp; apply to all active
          </Button>
          <Button variant="text" onClick={onApplySaved}>
            Apply saved default to all
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}
